import React from "react";
import { normalizeLinks } from "../../../../utils/clubLinks";
import { CLUB_APPLICATION_LIMITS as LIMIT, CLUB_APPLICATION_STORAGE_PREFIXES as PREFIX, isStorageUrlIn } from "../../../../firebase-functions/schema/clubApplication";
import { storage } from "../../firebase.web";
import { Avatar, Button, ClubLogo, Pill } from "../../components/primitives";

// pdfs are stored as Storage download URLs; recover the original file name from the object path.
function docName(url, i) {
  try {
    const last = decodeURIComponent(String(url).split("?")[0].split("/").pop() || "");
    return last.replace(/^club-doc-\d+-\d+-/, "") || `Document ${i + 1}`;
  } catch {
    return `Document ${i + 1}`;
  }
}

const str = (v) => (typeof v === "string" ? v : "");

/** One pending "New club" request (mockup viewSuper card + the app's logo/links/documents/gallery). */
export default function RequestCard({ req, pendingAction, onApprove, onReject }) {
  // clubData is written by the applicant and only `name` is checked by the rules, so every field is
  // read defensively (a malformed request must not crash the whole queue), and media is shown only
  // when it is a download URL in this project's bucket under the folder the approval callable keeps
  // (clubApproval.js) — never an arbitrary URL fetched by the reviewer's browser. Lists are capped at
  // the approval schema limits so an oversized request can't flood the page.
  const u = req.user || {};
  const c = req.clubData && typeof req.clubData === "object" ? req.clubData : {};
  const bucket = storage.app.options.storageBucket;
  const inFolder = (folder) => (url) => isStorageUrlIn(url, bucket, folder);
  const links = normalizeLinks(c.links).map((l) => ({ url: str(l.url), name: str(l.name) })).filter((l) => l.url || l.name).slice(0, LIMIT.list);
  const pdfs = Array.isArray(c.pdfs) ? [...new Set(c.pdfs.filter(inFolder(PREFIX.pdf)))].slice(0, LIMIT.list) : [];
  const gallery = Array.isArray(c.gallery) ? [...new Set(c.gallery.filter(inFolder(PREFIX.gallery(req.userId))))].slice(0, LIMIT.gallery) : [];
  const logoUrl = inFolder(PREFIX.logo)(c.logo_url) ? c.logo_url : null;
  const busy = !!pendingAction;
  return (
    <div className="card pad col" aria-busy={busy}>
      <div className="row">
        <Avatar name={str(u.full_name)} url={str(u.avatar_url) || null} id={req.userId} size={46} />
        <div className="grow">
          <b>{str(u.full_name) || "Unknown User"}</b>
          <div className="meta">{str(u.email) || "No email"} · {str(u.major) || "No major"} · {u.graduation_year ? String(u.graduation_year) : "No year"}</div>
        </div>
        <Pill tone="blue">New club</Pill>
      </div>
      <div className="col" style={{ background: "var(--subRow)", borderRadius: 12, padding: 12, fontSize: 13.5 }}>
        <div className="row" style={{ gap: 12 }}>
          <ClubLogo club={{ id: req.id, name: str(c.name), logo_url: logoUrl }} size={48} />
          <div>
            <b>{str(c.name) || "Not specified"}</b>
            {str(c.category) && <div className="muted">{str(c.category)}</div>}
          </div>
        </div>
        <div>{str(c.description) || "No description provided"}</div>
        {links.length > 0 && (
          <div className="muted">Links: {links.map((l) => l.name || l.url).join(", ")}</div>
        )}
        {pdfs.length > 0 && (
          <div className="muted">
            Documents:{" "}
            {pdfs.map((url, i) => (
              <React.Fragment key={url}>
                {i > 0 && ", "}
                <a className="link" href={url} target="_blank" rel="noopener noreferrer" data-testid={`superAdmin.request.${req.id}.document.${i}`}>{docName(url, i)}</a>
              </React.Fragment>
            ))}
          </div>
        )}
        {gallery.length > 0 && (
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {gallery.map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer" aria-label="Open gallery photo" data-testid={`superAdmin.request.${req.id}.photo`}>
                <img src={url} alt="" loading="lazy" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10 }} />
              </a>
            ))}
          </div>
        )}
        <div className="tiny muted">Submitted {str(req.created_at_formatted) || "Unknown date"}</div>
      </div>
      <div className="row">
        <Button variant="ok" size="sm" className="grow" disabled={busy} onClick={onApprove} data-testid={`superAdmin.request.${req.id}.approve`}>
          {pendingAction === "approve" ? "Approving…" : "Approve"}
        </Button>
        <Button variant="danger" size="sm" className="grow" disabled={busy} onClick={onReject} data-testid={`superAdmin.request.${req.id}.reject`}>
          {pendingAction === "reject" ? "Rejecting…" : "Reject"}
        </Button>
      </div>
    </div>
  );
}
